import React, { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Shield, Search, RefreshCw, CheckCircle, XCircle, Lock, Monitor, ChevronLeft, ChevronRight, AlertTriangle } from 'lucide-react';

const SecurityPage = () => {
  const [history, setHistory] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [filter, setFilter] = useState('');
  const [securityAlerts, setSecurityAlerts] = useState([]);
  const pageSize = 50;

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    try {
      const [histRes, alertsRes] = await Promise.all([
        api.get(`/auth/login-history?skip=${page * pageSize}&limit=${pageSize}`),
        api.get('/alerts/security').catch(() => ({ data: [] })),
      ]);
      setHistory(histRes.data.items || []);
      setTotal(histRes.data.total || 0);
      setSecurityAlerts(alertsRes.data || []);
    } catch {
      toast.error('Erreur lors du chargement de l\'historique');
    }
    setLoading(false);
  }, [page]);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  const getActionBadge = (action) => {
    switch (action) {
      case 'login':
        return <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 gap-1" data-testid="badge-success"><CheckCircle className="w-3 h-3" /> Connexion</Badge>;
      case 'login_failed':
        return <Badge className="bg-red-500/20 text-red-400 border-red-500/30 gap-1" data-testid="badge-failed"><XCircle className="w-3 h-3" /> Echec</Badge>;
      case 'login_locked':
        return <Badge className="bg-orange-500/20 text-orange-400 border-orange-500/30 gap-1" data-testid="badge-locked"><Lock className="w-3 h-3" /> Verrouillage</Badge>;
      default:
        return <Badge variant="outline">{action}</Badge>;
    }
  };

  const formatDate = (ts) => {
    if (!ts) return '-';
    const d = new Date(ts);
    return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  const parseUserAgent = (ua) => {
    if (!ua) return '-';
    if (ua.includes('Mobile')) return 'Mobile';
    if (ua.includes('Chrome')) return 'Chrome';
    if (ua.includes('Firefox')) return 'Firefox';
    if (ua.includes('Safari')) return 'Safari';
    if (ua.includes('curl')) return 'curl';
    return ua.substring(0, 30);
  };

  const filtered = filter
    ? history.filter(h =>
        (h.email || '').toLowerCase().includes(filter.toLowerCase()) ||
        (h.ip_address || '').includes(filter)
      )
    : history;

  const stats = {
    total: total,
    success: history.filter(h => h.action === 'login').length,
    failed: history.filter(h => h.action === 'login_failed').length,
    locked: history.filter(h => h.action === 'login_locked').length,
  };

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="space-y-6" data-testid="security-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold text-white flex items-center gap-2">
            <Shield className="w-6 h-6 text-amber-400" />
            Securite — Historique des connexions
          </h1>
          <p className="text-zinc-400 text-sm mt-1">Toutes les tentatives de connexion avec adresses IP</p>
        </div>
        <Button onClick={fetchHistory} variant="outline" size="sm" className="border-zinc-700 text-zinc-300 gap-2" data-testid="refresh-history">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Actualiser
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-4">
            <p className="text-zinc-500 text-xs">Total tentatives</p>
            <p className="text-2xl font-bold text-white mt-1" data-testid="stat-total">{stats.total}</p>
          </CardContent>
        </Card>
        <Card className="bg-emerald-500/5 border-emerald-500/20">
          <CardContent className="p-4">
            <p className="text-emerald-400/70 text-xs">Connexions reussies</p>
            <p className="text-2xl font-bold text-emerald-400 mt-1" data-testid="stat-success">{stats.success}</p>
          </CardContent>
        </Card>
        <Card className="bg-red-500/5 border-red-500/20">
          <CardContent className="p-4">
            <p className="text-red-400/70 text-xs">Echecs</p>
            <p className="text-2xl font-bold text-red-400 mt-1" data-testid="stat-failed">{stats.failed}</p>
          </CardContent>
        </Card>
        <Card className="bg-orange-500/5 border-orange-500/20">
          <CardContent className="p-4">
            <p className="text-orange-400/70 text-xs">Verrouillages</p>
            <p className="text-2xl font-bold text-orange-400 mt-1" data-testid="stat-locked">{stats.locked}</p>
          </CardContent>
        </Card>
      </div>

      {/* Security Alerts */}
      {securityAlerts.length > 0 && (
        <Card className="bg-red-500/5 border-red-500/30">
          <CardHeader className="pb-2">
            <CardTitle className="text-red-400 text-base flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Alertes de securite ({securityAlerts.filter(a => !a.is_read).length} non lues)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {securityAlerts.slice(0, 10).map((alert, i) => (
                <div key={alert.id || i} className={`flex items-start gap-3 p-2.5 rounded-lg border ${alert.is_read ? 'border-zinc-800 bg-zinc-900/30' : 'border-red-500/30 bg-red-500/10'}`}
                  data-testid={`security-alert-${i}`}>
                  <Lock className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm text-white">{alert.message}</p>
                    <p className="text-xs text-zinc-500 mt-0.5">{new Date(alert.created_at).toLocaleString('fr-FR')}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Table */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-white text-base">Journal des connexions</CardTitle>
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <Input
                placeholder="Filtrer par email ou IP..."
                className="pl-9 bg-zinc-950 border-zinc-800 text-white text-sm h-9"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                data-testid="filter-input"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center h-40">
              <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="login-history-table">
                  <thead>
                    <tr className="border-b border-zinc-800">
                      <th className="text-left py-3 px-3 text-zinc-400 text-xs font-semibold uppercase">Date / Heure</th>
                      <th className="text-left py-3 px-3 text-zinc-400 text-xs font-semibold uppercase">Statut</th>
                      <th className="text-left py-3 px-3 text-zinc-400 text-xs font-semibold uppercase">Email</th>
                      <th className="text-left py-3 px-3 text-zinc-400 text-xs font-semibold uppercase">Adresse IP</th>
                      <th className="text-left py-3 px-3 text-zinc-400 text-xs font-semibold uppercase">Navigateur</th>
                      <th className="text-left py-3 px-3 text-zinc-400 text-xs font-semibold uppercase">Tentatives</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.length > 0 ? filtered.map((entry, i) => (
                      <tr key={i} className="border-b border-zinc-800/50 hover:bg-zinc-800/30 transition-colors" data-testid={`login-row-${i}`}>
                        <td className="py-2.5 px-3 text-zinc-300 text-sm font-mono">{formatDate(entry.timestamp)}</td>
                        <td className="py-2.5 px-3">{getActionBadge(entry.action)}</td>
                        <td className="py-2.5 px-3 text-white text-sm">{entry.email || '-'}</td>
                        <td className="py-2.5 px-3">
                          <span className="font-mono text-sm text-cyan-400 bg-cyan-500/10 px-2 py-0.5 rounded" data-testid={`ip-${i}`}>
                            {entry.ip_address || '-'}
                          </span>
                        </td>
                        <td className="py-2.5 px-3 text-zinc-400 text-sm flex items-center gap-1.5">
                          <Monitor className="w-3.5 h-3.5" />
                          {parseUserAgent(entry.user_agent)}
                        </td>
                        <td className="py-2.5 px-3 text-zinc-400 text-sm">
                          {entry.attempts ? `${entry.attempts}/5` : '-'}
                        </td>
                      </tr>
                    )) : (
                      <tr>
                        <td colSpan={6} className="py-12 text-center text-zinc-500">
                          {filter ? 'Aucun resultat pour ce filtre' : 'Aucune tentative de connexion enregistree'}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-zinc-800">
                  <p className="text-zinc-500 text-sm">
                    Page {page + 1} / {totalPages} ({total} entrees)
                  </p>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
                      className="border-zinc-700 text-zinc-300" data-testid="prev-page">
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} disabled={page >= totalPages - 1}
                      className="border-zinc-700 text-zinc-300" data-testid="next-page">
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SecurityPage;
